import { RotateCwIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Progress } from '@/shared/ui/progress';

import { FileIcon } from './FileIcon';
import type { FileUploadsState } from './useFileUploads';

/** In-flight uploads (progress, cancel, retry) and validation messages of a `useFileUploads` state. */
export function UploadTaskList({ uploads, className }: { uploads: FileUploadsState; className?: string }) {
  const { t } = useTranslation('shared');
  const { tasks, rejected, retry, cancel } = uploads;
  if (tasks.length === 0 && rejected.length === 0) return null;
  return (
    <div className={cn('space-y-1.5', className)}>
      {rejected.length > 0 && (
        <ul className="space-y-0.5 text-xs text-destructive-text" role="alert">
          {rejected.map((message, i) => (
            <li key={i}>{message}</li>
          ))}
        </ul>
      )}
      {tasks.length > 0 && (
        <ul className="space-y-1.5" aria-live="polite">
          {tasks.map((task) => (
            <li key={task.key} className="flex items-center gap-3 rounded-lg border bg-card p-2 pr-2.5">
              <FileIcon name={task.file.name} contentType={task.file.type} size="sm" />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-medium">{task.file.name}</span>
                  <span
                    className={cn(
                      'shrink-0 tabular-nums',
                      task.status === 'error' ? 'text-destructive-text' : 'text-muted-foreground',
                    )}
                  >
                    {task.status === 'error' ? t('files.failed') : `${task.progress}%`}
                  </span>
                </div>
                {task.status === 'uploading' ? (
                  <>
                    <Progress value={task.progress} aria-label={task.file.name} />
                    {task.note && (
                      <p className="truncate text-xs text-muted-foreground" data-testid="upload-note">
                        {task.note}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="truncate text-xs text-muted-foreground">{task.error}</p>
                )}
              </div>
              {task.status === 'error' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => retry(task)}
                  aria-label={t('actions.retry')}
                >
                  <RotateCwIcon />
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => cancel(task)}
                aria-label={task.status === 'uploading' ? t('files.cancelUpload') : t('actions.remove')}
              >
                <XIcon />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
